import React, { useEffect, useRef, useState } from 'react'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Button, Input, InputRef, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { FilterValue } from 'antd/es/table/interface'
import { path } from '../../../libs'
import CIcon from '@coreui/icons-react'
import { cilDelete, cilZoom } from '@coreui/icons'
import { DataIndex, Gateway, WithdrawalGroup } from './types'
import { useWithdrawalGroups } from './hooks'
import { deleteGatewayEvent } from '../../gateway/list/model'
import { deleteWithdrawalGroup, deleteWithdrawalGroupEvent } from './model'
export const WithdrawalGroupList = () => {
  const {
    withdrawalGroups,
    totalCountOfWithdrawalGroups,
    loading,
    onChangeWithdrawalGroupsList,
  } = useWithdrawalGroups()

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  useEffect(() => {
    onChangeWithdrawalGroupsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<WithdrawalGroup> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => name,
    },
    {
      title: 'Gateways',
      dataIndex: 'gateways',
      key: 'gateways',
      render: (item: Gateway[], record) => {
        return (
          <ul>
            {item.map((item) => (
              <ol>
                <a
                  href={path.billing.gateway.edit(item.gatewayId)}
                  target="_blank"
                >
                  {item.gatewayId}
                </a>
              </ol>
            ))}
          </ul>
        )
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.billing.paymentGroup.edit(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
            <CButton
              color="danger"
              className="me-2"
              title={'Delete'}
              onClick={() => {
                if (
                  window.confirm('Are you sure you wish to delete this item?')
                )
                  deleteWithdrawalGroupEvent(item.id)
              }}
            >
              <CIcon icon={cilDelete} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <CButton
        color="primary"
        className="mb-2"
        href={path.billing.paymentGroup.create()}
      >
        Create new group
      </CButton>
      <Table
        dataSource={withdrawalGroups}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="giftId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfWithdrawalGroups,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      ></Table>
    </CCardBody>
  )
}
